import {Component, Injector, OnInit} from '@angular/core';
import {BaseComponent} from "../base/base.component";
import {Category} from "../../models/category";
import {CategoryService} from "../../services/category/category.service";
import {Season} from "../../models/season";
import {SeasonService} from "../../services/season/season.service";
import Swal from "sweetalert2";
import * as moment from "moment";

type SeasonPhase = 'current' | 'upcoming' | 'past' | 'ongoing';

interface SeasonForm {
    id?: string;
    name: string;
    startDate: string;
    endDate: string;
}

interface CategoryForm {
    id?: string;
    name: string;
    parentId?: string;
    fullPrice: number | null;
    reducedPrice: number | null;
}

interface ParentCategoryGroup {
    category: Category;
    children: Category[];
}

const VISIBLE_SEASONS = 4;

@Component({
    selector: 'app-categories-list',
    templateUrl: './categories-list.component.html',
    styleUrls: ['./categories-list.component.scss']
})
export class CategoriesListComponent extends BaseComponent implements OnInit {

    companyId: string = localStorage.getItem('wisper_companyId') ?? '';

    seasons: Season[] = [];
    selectedSeasonId?: string;
    groups: ParentCategoryGroup[] = [];
    seasonSearch = '';

    loadingSeasons = true;
    loadingCategories = true;
    saving = false;

    seasonForm: SeasonForm = this.emptySeasonForm();
    categoryForm: CategoryForm = this.emptyCategoryForm();
    // Parent categories edit the name only; subcategories also carry prices.
    categoryFormKind: 'parent' | 'child' = 'parent';

    constructor(
        public serviceInjector: Injector,
        public categoryService: CategoryService,
        public seasonService: SeasonService,
    ) {
        super(serviceInjector);
    }

    async ngOnInit() {
        await this.loadSeasons();
        await this.loadCategories();
    }

    get selectedSeason(): Season | undefined {
        return this.seasons.find(season => season.id === this.selectedSeasonId);
    }

    get currentSeason(): Season | undefined {
        return this.seasons.find(season => !!season.isCurrent);
    }

    // The dashboard shows the latest seasons only; the current and the selected one are always kept visible.
    get visibleSeasons(): Season[] {
        const visible = this.seasons.slice(0, VISIBLE_SEASONS);
        for (const pinned of [this.currentSeason, this.selectedSeason]) {
            if (!pinned || visible.includes(pinned)) continue;
            const replaceAt = visible.map(season => season === this.currentSeason || season === this.selectedSeason)
                .lastIndexOf(false);
            if (replaceAt >= 0) visible[replaceAt] = pinned;
        }
        return visible.sort((a, b) => this.seasons.indexOf(a) - this.seasons.indexOf(b));
    }

    get filteredSeasons(): Season[] {
        const query = this.seasonSearch.trim().toLowerCase();
        return query ? this.seasons.filter(season => season.name.toLowerCase().includes(query)) : this.seasons;
    }

    get subcategoryCount(): number {
        return this.groups.reduce((total, group) => total + group.children.length, 0);
    }

    // ---- Loading ----

    async loadSeasons() {
        this.loadingSeasons = true;
        const response = await this.seasonService.getSeasons(this.companyId, {$orderby: 'startDate desc'});
        this.loadingSeasons = false;

        if (!response?.success) return;

        this.seasons = Season.fromApiList(response.data);

        // Keep the user's selection across reloads; fall back to the current season.
        if (!this.seasons.some(season => season.id === this.selectedSeasonId)) {
            this.selectedSeasonId = (this.currentSeason ?? this.seasons[0])?.id;
        }
    }

    async loadCategories() {
        if (!this.selectedSeasonId) {
            this.groups = [];
            this.loadingCategories = false;
            return;
        }

        this.loadingCategories = true;
        const seasonId = this.selectedSeasonId;
        const categories = (await this.categoryService.getCategories(this.companyId, false, {seasonId}))
            .map(item => new Category(item));

        // Ignore a stale response if the user switched season meanwhile.
        if (seasonId !== this.selectedSeasonId) return;

        this.groups = categories
            .filter(category => !category.parentId)
            .map(category => ({
                category,
                children: categories.filter(child => child.parentId === category.id),
            }));
        this.loadingCategories = false;
    }

    async selectSeason(season: Season) {
        if (season.id === this.selectedSeasonId) return;
        this.selectedSeasonId = season.id;
        await this.loadCategories();
    }

    // ---- Seasons ----

    seasonPhase(season: Season): SeasonPhase {
        if (season.isCurrent) return 'current';
        const today = moment().startOf('day');
        if (season.startDate && moment(season.startDate).isAfter(today)) return 'upcoming';
        if (season.endDate && moment(season.endDate).isBefore(today)) return 'past';
        return 'ongoing';
    }

    seasonPhaseLabel(season: Season): string {
        return {
            current: 'In corso',
            upcoming: 'Futura',
            past: 'Conclusa',
            ongoing: 'Non corrente',
        }[this.seasonPhase(season)];
    }

    seasonPhaseClasses(season: Season): string {
        return {
            current: 'bg-emerald-100 text-emerald-800',
            upcoming: 'bg-sky-100 text-sky-800',
            past: 'bg-slate-100 text-slate-500',
            ongoing: 'bg-slate-100 text-slate-500',
        }[this.seasonPhase(season)];
    }

    seasonPeriod(season: Season): string {
        if (!season.startDate || !season.endDate) return '';
        return `${moment(season.startDate).format('DD/MM/YYYY')} – ${moment(season.endDate).format('DD/MM/YYYY')}`;
    }

    openAllSeasons() {
        this.seasonSearch = '';
        this.ngxSmartModalService.getModal('allSeasonsModal').open();
    }

    async openSeasonFromList(season: Season) {
        this.ngxSmartModalService.getModal('allSeasonsModal').close();
        await this.selectSeason(season);
    }

    openSeasonModal(season?: Season) {
        this.seasonForm = season
            ? {
                id: season.id,
                name: season.name,
                startDate: season.startDate ? this.formatDateInput(season.startDate) : '',
                endDate: season.endDate ? this.formatDateInput(season.endDate) : '',
            }
            : this.suggestNextSeason();
        this.ngxSmartModalService.getModal('seasonModal').open();
    }

    get seasonFormError(): string | null {
        const {startDate, endDate} = this.seasonForm;
        if (startDate && endDate && moment(endDate).isSameOrBefore(moment(startDate), 'day')) {
            return 'La data di fine deve essere successiva alla data di inizio.';
        }
        return null;
    }

    get seasonDurationLabel(): string {
        const {startDate, endDate} = this.seasonForm;
        if (!startDate || !endDate) return '';
        const months = Math.round(moment(endDate).add(1, 'day').diff(moment(startDate), 'months', true));
        return months > 0 ? `Durata: ${months} ${months === 1 ? 'mese' : 'mesi'}` : '';
    }

    get isSeasonFormValid(): boolean {
        const {name, startDate, endDate} = this.seasonForm;
        return !!name.trim() && !!startDate && !!endDate && !this.seasonFormError;
    }

    async saveSeason() {
        if (!this.isSeasonFormValid || this.saving) return;

        const isNew = !this.seasonForm.id;
        const payload = new Season();
        payload.id = this.seasonForm.id;
        payload.name = this.seasonForm.name.trim();
        payload.startDate = this.seasonForm.startDate;
        payload.endDate = this.seasonForm.endDate;
        payload.companyId = this.companyId;

        this.saving = true;
        const response = isNew
            ? await this.seasonService.addSeason(payload)
            : await this.seasonService.updateSeason(payload);
        this.saving = false;

        if (!response?.success) {
            this.notifyError(isNew ? 'Errore durante la creazione della stagione' : "Errore durante l'aggiornamento della stagione");
            return;
        }

        this.ngxSmartModalService.getModal('seasonModal').close();
        if (isNew && response.data?.id) this.selectedSeasonId = response.data.id;
        this.notifySuccess(isNew ? 'Stagione creata' : 'Stagione aggiornata');
        await this.loadSeasons();
        await this.loadCategories();
    }

    async makeSeasonCurrent(season: Season) {
        const current = this.currentSeason;
        const result = await Swal.fire({
            icon: 'question',
            title: `Rendere "${season.name}" la stagione corrente?`,
            html: current
                ? `Le nuove iscrizioni useranno le categorie di <b>${this.escapeHtml(season.name)}</b>.<br>` +
                  `<b>${this.escapeHtml(current.name)}</b> resterà consultabile ma non sarà più la stagione corrente.`
                : `Le nuove iscrizioni useranno le categorie di <b>${this.escapeHtml(season.name)}</b>.`,
            showCancelButton: true,
            confirmButtonText: 'Sì, rendi corrente',
            cancelButtonText: 'Annulla',
        });
        if (!result.isConfirmed) return;

        this.spinner.show();
        const response = await this.seasonService.setSeasonStatus(season.id!);
        this.spinner.hide();

        if (!response?.success) {
            this.notifyError(response?.code === 'SEASON_ALREADY_CURRENT'
                ? 'Questa stagione è già quella corrente'
                : "Errore durante l'aggiornamento della stagione corrente");
            return;
        }

        this.notifySuccess(`"${season.name}" è ora la stagione corrente`);
        this.selectedSeasonId = season.id;
        await this.loadSeasons();
        await this.loadCategories();
    }

    // ---- Categories ----

    openParentCategoryModal(category?: Category) {
        this.categoryFormKind = 'parent';
        this.categoryForm = category
            ? {id: category.id, name: category.name, fullPrice: null, reducedPrice: null}
            : this.emptyCategoryForm();
        this.ngxSmartModalService.getModal('categoryModal').open();
    }

    openSubcategoryModal(parent: Category, category?: Category) {
        this.categoryFormKind = 'child';
        this.categoryForm = category
            ? {
                id: category.id,
                name: category.name,
                parentId: category.parentId,
                fullPrice: category.fullPrice,
                reducedPrice: category.reducedPrice,
            }
            : {...this.emptyCategoryForm(), parentId: parent.id};
        this.ngxSmartModalService.getModal('categoryModal').open();
    }

    get categoryModalTitle(): string {
        const isNew = !this.categoryForm.id;
        if (this.categoryFormKind === 'parent') return isNew ? 'Nuova categoria' : 'Modifica categoria';
        return isNew ? 'Nuova sottocategoria' : 'Modifica sottocategoria';
    }

    get parentOptions(): Category[] {
        return this.groups.map(group => group.category);
    }

    get categoryFormError(): string | null {
        if (this.categoryFormKind !== 'child') return null;
        const {fullPrice, reducedPrice} = this.categoryForm;
        if ((fullPrice ?? 0) < 0 || (reducedPrice ?? 0) < 0) return 'I prezzi non possono essere negativi.';
        if (fullPrice != null && reducedPrice != null && reducedPrice > fullPrice) {
            return 'Il prezzo ridotto non può superare il prezzo intero.';
        }
        return null;
    }

    get isCategoryFormValid(): boolean {
        const form = this.categoryForm;
        if (!form.name.trim() || this.categoryFormError) return false;
        if (this.categoryFormKind === 'parent') return true;
        return !!form.parentId && form.fullPrice != null;
    }

    async saveCategory() {
        if (!this.isCategoryFormValid || this.saving || !this.selectedSeasonId) return;

        const form = this.categoryForm;
        const isNew = !form.id;
        const payload = new Category();
        payload.id = form.id;
        payload.name = form.name.trim();
        payload.seasonId = this.selectedSeasonId;
        payload.companyId = this.companyId;
        if (this.categoryFormKind === 'child') {
            payload.parentId = form.parentId;
            payload.fullPrice = form.fullPrice ?? 0;
            // An empty reduced price means "no discount".
            payload.reducedPrice = form.reducedPrice ?? payload.fullPrice;
        } else if (!isNew) {
            // Parent categories have no price of their own: don't touch the stored ones on rename.
            delete (payload as Partial<Category>).fullPrice;
            delete (payload as Partial<Category>).reducedPrice;
        }

        this.saving = true;
        const success = isNew
            ? await this.categoryService.createCategory(payload)
            : await this.categoryService.updateCategory(payload);
        this.saving = false;

        if (!success) {
            this.notifyError(isNew ? 'Errore nella creazione della categoria' : "Errore nell'aggiornamento della categoria");
            return;
        }

        this.ngxSmartModalService.getModal('categoryModal').close();
        this.notifySuccess(isNew ? 'Categoria creata' : 'Categoria aggiornata');
        await this.loadCategories();
    }

    async deleteCategory(category: Category, isParent = false) {
        const result = await Swal.fire({
            icon: 'warning',
            title: `Eliminare "${category.name}"?`,
            text: isParent
                ? 'La categoria verrà rimossa da questa stagione.'
                : 'La sottocategoria non sarà più disponibile per le nuove iscrizioni.',
            showCancelButton: true,
            confirmButtonText: 'Sì, elimina',
            cancelButtonText: 'Annulla',
        });
        if (!result.isConfirmed) return;

        const success = await this.categoryService.deleteCategory(category.id!);
        if (!success) {
            this.notifyError("Errore nell'eliminazione della categoria");
            return;
        }

        this.notifySuccess('Categoria eliminata');
        await this.loadCategories();
    }

    formatPrice(value: number): string {
        return (value ?? 0).toLocaleString('it-IT', {style: 'currency', currency: 'EUR'});
    }

    trackById(_: number, item: {id?: string} | ParentCategoryGroup) {
        return 'category' in item ? item.category.id : item.id;
    }

    // ---- Helpers ----

    private emptySeasonForm(): SeasonForm {
        return {name: '', startDate: '', endDate: ''};
    }

    private emptyCategoryForm(): CategoryForm {
        return {name: '', fullPrice: null, reducedPrice: null};
    }

    // Pre-fill a new season right after the latest one (e.g. "2026/2027", 1 Sep – 31 Aug).
    private suggestNextSeason(): SeasonForm {
        const latestEnd = this.seasons
            .map(season => season.endDate ? moment(season.endDate) : null)
            .filter((date): date is moment.Moment => !!date && date.isValid())
            .sort((a, b) => b.valueOf() - a.valueOf())[0];

        const start = latestEnd
            ? latestEnd.clone().add(1, 'day')
            : moment().month() >= 6 ? moment().month(8).date(1) : moment().subtract(1, 'year').month(8).date(1);
        const end = start.clone().add(1, 'year').subtract(1, 'day');

        return {
            name: `${start.year()}/${end.year()}`,
            startDate: start.format('YYYY-MM-DD'),
            endDate: end.format('YYYY-MM-DD'),
        };
    }

    private notifySuccess(title: string) {
        Swal.fire({icon: 'success', title, timer: 1600, showConfirmButton: false});
    }

    private notifyError(title: string) {
        Swal.fire({icon: 'error', title});
    }

    private escapeHtml(value: string): string {
        return value.replace(/[&<>"']/g, char => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[char]!));
    }
}
